<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
  >
    <template #activator="{ props: dialogProps }">
      <v-tooltip location="top">
        <template #activator="{ props: tooltipProps }">
          <v-btn
            v-if="hasPermission('create ticket::add-payment')"
            key="checking-btn"
            :color="limitReached ? 'error' : 'primary'"
            class="mr-2"
            dark
            fab
            v-bind="{ ...dialogProps, ...tooltipProps }"
            @click="populateDefaultValues()"
          >
            <v-icon dark>
              mdi-bank-plus
            </v-icon>
          </v-btn>
          <disabled-for-lack-of-role-btn
            v-else
            key="checking-disabled"
            text=""
            icon="mdi-bank-plus"
            permission="create ticket::add-payment"
            :x-small="false"
            button-class="mr-2"
          />
        </template>
        <span>Add a New Checking or Savings Account</span>
      </v-tooltip>
    </template>

    <v-card v-if="limitReached">
      <v-card-title class="text-h5 grey lighten-2">
        Add a New Checking or Savings Account
      </v-card-title>
      <v-card-text
        class="mt-6"
      >
        <v-alert
          border="top"
          color="red lighten-2"
          dark
        >
          Payment Limit Reached, please delete some account before add new one!
        </v-alert>
      </v-card-text>
      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          class="mr-2"
          @click="closeAll()"
        >
          Cancel
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-card v-else>
      <v-card-title class="text-h5 grey lighten-2">
        Add a New Checking or Savings Account
      </v-card-title>

      <v-card-text
        class="mt-6"
      >
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
        >
          <v-row
            cols="12"
          >
            <v-col
              cols="8"
            >
              <v-text-field
                v-model="checking_account.bank_name"
                label="Bank Name"
                :rules="bankNameRules"
                dense
                filled
                required
                disabled
              />
            </v-col>
            <v-col
              cols="4"
            >
              <v-select
                v-model="checking_account.account_type"
                label="Account Type"
                :rules="accountTypeRules"
                dense
                filled
                clearable
                required
                :items="account_types"
              />
            </v-col>
            <v-col
              cols="6"
            >
              <v-text-field
                v-model="checking_account.routing_number"
                label="Routing Number"
                :rules="routingNumberRules"
                dense
                filled
                clearable
                required
                @input="getBankName()"
              />
            </v-col>
            <v-col
              cols="6"
            >
              <v-text-field
                v-model="confirm_routing_number"
                label="Confirm Routing Number"
                :rules="confirmRoutingNumberRules"
                dense
                filled
                clearable
                required
              />
            </v-col>
            <v-col
              cols="6"
            >
              <v-text-field
                v-model="checking_account.account_number"
                label="Account Number"
                :rules="accountNumberRules"
                dense
                filled
                clearable
                required
              />
            </v-col>
            <v-col
              cols="6"
            >
              <v-text-field
                v-model="confirm_account_number"
                label="Confirm Account Number"
                :rules="confirmAccountNumberRules"
                dense
                filled
                clearable
                required
              />
            </v-col>
            <v-col
              cols="12"
            >
              <v-text-field
                v-model="checking_account.accountholder_name"
                label="Account Holder Name"
                :rules="accountHolderNameRules"
                dense
                filled
                clearable
                required
              />
            </v-col>
            <v-col
              cols="12"
            >
              <v-text-field
                v-model="checking_account.address"
                label="Address"
                :rules="addressRules"
                dense
                filled
                clearable
                required
              />
            </v-col>
            <v-col
              cols="4"
            >
              <v-text-field
                v-model="checking_account.city"
                label="City"
                :rules="cityRules"
                :disabled="zipcodeValidated"
                dense
                filled
                required
              />
            </v-col>
            <v-col
              cols="4"
            >
              <v-select
                v-model="checking_account.state"
                label="State"
                :rules="stateRules"
                :disabled="zipcodeValidated"
                dense
                filled
                required
                :items="states"
              />
            </v-col>
            <v-col
              cols="4"
            >
              <v-text-field
                v-model="checking_account.zipcode"
                label="Zip Code"
                :rules="zipcodeRules"
                dense
                filled
                clearable
                required
                @input="getCityState()"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <FuturePaymentQuestionDialog
        v-model="futureDialog"
        :account-type="`${checking_account.account_type} account`"
        :ticket="ticket"
        :payment-profile="mapPaymentProfile"
        @use-for-future-payments="save(true)"
        @no-future-payments="save(false)"
      />

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          class="mr-2"
          @click="closeDialog()"
        >
          Cancel
        </v-btn>
        <v-btn
          v-if="!loading"
          color="primary"
          dark
          :disabled="loading"
          @click.stop="askFutureQuestion()"
        >
          Add {{ checking_account.account_type }} Account
        </v-btn>
        <v-progress-circular
          v-else
          indeterminate
          color="primary"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { DateTime } from 'luxon';
import stateList from '@/data/states.json';

import FuturePaymentQuestionDialog from './FuturePaymentQuestionDialog.vue';
import DisabledForLackOfRoleBtn from '../../DisabledForLackOfRoleBtn.vue';

export default {
  name: 'TicketAddChecking',
  components: {
    FuturePaymentQuestionDialog,
    DisabledForLackOfRoleBtn,
  },
  props: {
    ticket: {
      type: Object,
      required: true,
    },
  },
  emits: ['success'],
  data() {
    return {
      zipcodeValidated: true, // force zip code attempt first
      futureDialog: false,
      valid: true,
      dialog: false,
      loading: false,
      checking_account: {
        accountholder_name: '',
        address: '',
        city: '',
        state: '',
        zipcode: '',
        country: 'USA',
        account_number: '',
        routing_number: '',
        bank_name: '',
        account_type: '',
      },
      accountHolderNameRules: [
        (v) => !!v || 'Account Holder name is required',
        (v) => (v && v.length >= 2) || 'Account Holder name is required',
      ],
      addressRules: [
        (v) => !!v || 'Address is required',
      ],
      cityRules: [
        (v) => !!v || 'City is required',
      ],
      stateRules: [
        (v) => !!v || 'State is required',
        (v) => (v && v.length >= 2) || 'State is required',
      ],
      zipcodeRules: [
        (v) => !!v || 'Zip Code is required',
        (v) => (v && v.length === 5) || 'Zip Code should be 5 digits long',
      ],
      accountNumberRules: [
        (v) => !!v || 'Account Number is required',
      ],
      routingNumberRules: [
        (v) => !!v || 'Routing Number is required',
        (v) => (v && v.length === 9) || 'Routing Number should be 9 digits',
      ],
      bankNameRules: [
        (v) => !!v || 'Bank Name is Required',
      ],
      accountTypeRules: [
        (v) => !!v || 'Account Type is required',
        (v) => (v && (v === 'checking' || v === 'savings')) || 'Account Type must equal "Checking" or "Savings"',
      ],
      confirm_account_number: '',
      confirmAccountNumberRules: [
        (v) => !!v || 'Please confirm your Account Number',
        (v) => (v && v === this.checking_account.account_number) || 'Does not match Account Number',
      ],
      confirm_routing_number: '',
      confirmRoutingNumberRules: [
        (v) => !!v || 'Please confirm your Routing Number',
        (v) => (v && v === this.checking_account.routing_number) || 'Does not match Routing Number',
      ],
      account_types: [
        {
          title: 'Checking',
          value: 'checking',
        },
        {
          title: 'Savings',
          value: 'savings',
        },
      ],
    };
  },
  computed: {
    ...mapGetters('loanProLoan', ['recurringAutopays']),
    states() {
      return stateList.map((state) => {
        const stateForSelect = {
          title: state.name,
          value: state.id,
        };
        return stateForSelect;
      });
    },

    mapPaymentProfile() {
      return {
        type: 'paymentAccount.type.checking',
        title: this.checking_account?.account_number ?? '',
        checking_account: {
          routing_number: this.checking_account?.routing_number ?? '',
        },
      };
    },

    hasAutopay() {
      return this.recurringAutopays.length > 0;
    },

    limitReached() {
      if (!this.settings.brand.max_payment_methods) {
        return false;
      }

      return this.paymentProfiles.length >= this.settings.brand.max_payment_methods;
    },
    ...mapState('loanProCustomer', ['customer', 'paymentProfiles']),
    ...mapState('settlement', ['disclosuresSent']),
    ...mapState('loanProLoan', ['todayScheduledPayment', 'autopays']),
    ...mapState('settings', ['settings']),
  },
  async created() {
    this.populateDefaultValues();
  },
  methods: {
    ...mapActions('notification', ['setNotification']),
    ...mapActions('loanProCustomer', ['getPaymentProfiles']),
    ...mapActions('autopays', ['updateAutopayPayment', 'reversePayment', 'makePayment', 'createLoanRecurringAutopay']),
    ...mapActions('settlement', ['disclosuresNotSent']),

    async askFutureQuestion() {
      await this.$refs.form.validate();
      if (this.valid) {
        await this.disclosuresNotSent();
        this.futureDialog = true;
      }
    },
    getBankName() {
      if (
        this.checking_account.routing_number === null
        || this.checking_account.routing_number.length !== 9
      ) {
        this.checking_account.bank_name = '';
      } else {
        this.$axios
          .get(`api/v1/admin/customer/bank-account/lookup?routingNumber=${this.checking_account.routing_number}`)
          .then((res) => {
            this.checking_account.bank_name = res.data.bankName;
          });
      }
    },
    async getCityState() {
      if (
        this.checking_account.zipcode === null
        || this.checking_account.zipcode.length !== 5
      ) {
        this.checking_account.city = '';
        this.checking_account.state = '';

        // force zip lookup before allowing manual entry
        this.zipcodeValidated = true;
      } else {
        try {
          const res = await this.$axios
            .get(`api/v1/admin/zip-code/lookup?zipCode=${this.checking_account.zipcode}`);

          // zip lookup succeeded, do not allow manual entry
          this.zipcodeValidated = true;
          this.checking_account.city = res.data.city;
          this.checking_account.state = res.data.state_abbr;
        } catch (error) {
          // no city/state found in db, allow manual entry
          this.zipcodeValidated = false;
        }
      }
    },
    // This method will make multiple API calls
    async save(future) {
      await this.$refs.form.validate();
      if (this.valid) {
        try {
          this.futureDialog = false;
          await this.createPaymentProfile(future);
        } catch (e) {
          await this.returnErrorNotification();
        }
      }
    },
    async createPaymentProfile(future) {
      let response;
      this.loading = true;
      // 1st API call, Generate token with form information
      const res = await this.$axios
        .post('api/v1/admin/pci-wallet', {
          endpoint: 'checking-account',
          httpMethod: 'post',
          payload: {
            'checking-account': {
              ...this.checking_account,
            },
          },
        });
      if (!res.data.result) {
        // return error notification
        await this.returnErrorNotification(`${res.data.message}.`);
      } else if (res.data.result) {
        // format bank account title:
        const lastFour = this.checking_account.account_number
          .substr(this.checking_account.account_number.length - 4);
        const dateInSeconds = DateTime.local()
          .toSeconds();
        const dateInSecondsString = `${dateInSeconds}`;
        const dateWithNoDecimals = dateInSecondsString.split('.');
        const accountTitle = `BankAcct${dateWithNoDecimals[0]} - ${lastFour}`;
        // 2nd API call, Send token to bank account API
        const bankResponse = await this.$axios
          .post('api/v1/admin/customer/bank-account', {
            bankAccountType: this.checking_account.account_type,
            title: accountTitle,
            token: res.data.data.token,
            customerId: this.ticket.lpCustomer.id,
            ticketId: this.ticket.crm.id,
          });
        if (bankResponse.status === 200) {
          // 3rd API call, done through store, Grab all Payment Profiles to verify
          // that this payment profile was created successfully
          await this.getPaymentProfiles();
          const newPaymentProfile = (this.customer?.paymentProfiles || [])
            .find((item) => item.title === accountTitle);
          if (newPaymentProfile) {
            // return success notification
            if (future) {
              // if theres an autopay for today (not Early Payment) and
              // the time is before 3pm (15:00)
              // reverse that autopay and create a new autopay with the new payment account
              if (this.todayScheduledPayment?.id) {
                const isEarlyPayment = this.autopays.find(
                  (autopay) => autopay.id === this.todayScheduledPayment.autopay_id && autopay.name === 'Early Portal Payment',
                );

                if (!isEarlyPayment && DateTime.local() < DateTime.local().startOf('day').plus({ hours: 15 })) {
                  const reverseRes = await this.reversePayment(this.todayScheduledPayment.id);
                  if (reverseRes.status === 200) {
                    // make new payment for reversed payment using the new payment info
                    await this.makePayment({
                      paymentProfile: newPaymentProfile.id,
                      paymentDate: this.todayScheduledPayment.apply_date,
                      paymentAmount: this.todayScheduledPayment.amount,
                      paymentTypeId: this.todayScheduledPayment.payment_type_id,
                    });
                  }
                }
              }

              if (this.hasAutopay) {
                response = await this.updateAutopayPayment(
                  {
                    loanId: this.ticket.active_loan.id,
                    selectedAccountId: newPaymentProfile.id,
                  },
                );
              } else {
                response = await this.createLoanRecurringAutopay({
                  loanId: this.ticket.active_loan.id,
                  paymentAccountId: newPaymentProfile.id,
                });
              }

              if (response.status === 200) {
                // update the payment profiles list to reflect new primary
                await this.getPaymentProfiles();
                await this.returnSuccessNotification(accountTitle, 'We successfully created the Payment Profile. Note: This profile set as the payment profile for the existing recurring autopay.');
              } else {
                // still update the list to show the newly added profile
                await this.getPaymentProfiles();
                await this.returnErrorNotification('In order to set a primary payment method, there must be an active autopay. Please set up an autopay and try again.');
                this.dialog = false;
              }
            } else {
              // return error notification
              await this.returnSuccessNotification(accountTitle, 'We successfully created the Payment Profile. Note: This profile was not set as the primary payment account.');
            }
          } else {
            // return error notification
            await this.returnErrorNotification('We were unable to create the Payment Profile. Please try again. If this message persists, contact your supervisor.');
          }
        } else {
          // return error notification
          await this.returnErrorNotification('We were unable to create a Bank Account token with PCI wallet. Please try again. If this message persists, contact your supervisor.');
        }
      }
    },
    async returnErrorNotification(message = 'Something went wrong') {
      // add comment to Zendesk controller
      await this.$axios
        .post('api/v1/tickets/add-ticket-comment', {
          ticketId: this.$route.params.id,
          comment: `${message}`,
        });
      this.setNotification({
        message,
        color: 'red',
      });
      this.loading = false;
    },
    async returnSuccessNotification(accountTitle, message = 'We have successfully added this Payment Method') {
      // add comment to Zendesk controller
      await this.$axios
        .post('api/v1/tickets/add-ticket-comment', {
          ticketId: this.$route.params.id,
          comment: `${message} Here is the Bank Account title: ${accountTitle}`,
        });
      this.setNotification({
        message,
        color: 'green',
      });
      this.loading = false;
      this.$refs.form.resetValidation();
      // emit a success event with new payment accountTitle
      this.$emit('success', accountTitle);
      this.dialog = false;
    },
    closeDialog() {
      this.$refs.form.resetValidation();
      this.closeAll();
    },
    closeAll() {
      this.dialog = false;
    },
    populateDefaultValues() {
      // clear everything to be safe
      this.checking_account.bank_name = '';
      this.checking_account.account_type = '';
      this.checking_account.routing_number = '';
      this.confirm_routing_number = '';
      this.checking_account.account_number = '';
      this.confirm_account_number = '';
      this.checking_account.accountholder_name = '';
      this.checking_account.address = '';
      this.checking_account.city = '';
      this.checking_account.state = '';
      this.checking_account.zipcode = '';

      // set customer values where appropriate
      this.checking_account.accountholder_name = `${this.ticket?.customer?.firstname || ''} ${this.ticket?.customer?.lastname || ''}`;
      this.checking_account.address = this.ticket?.customer?.address || '';
      this.checking_account.city = this.ticket?.customer?.city || '';
      this.checking_account.state = this.ticket?.customer?.state || '';
      this.checking_account.zipcode = this.ticket?.customer?.zip || '';
    },
  },
};
</script>
